import React, { useState } from 'react';
import VimshottariDashaComplete from './VimshottariDashaComplete';

/**
 * Complete Dasha Systems Display Component
 * Shows all 9 Dasha systems with beautiful visualization
 */
const DashaSystemsComplete = ({ data }) => {
  const [selectedSystem, setSelectedSystem] = useState('vimshottari');
  const [expandedMD, setExpandedMD] = useState(null);

  if (!data || Object.keys(data).length === 0) {
    return <div className="p-4 bg-gray-50 rounded">No Dasha data available</div>;
  }

  const dashaSystemsInfo = [
    {
      id: 'vimshottari',
      key: '11.1_vimshottari_md',
      adKey: '11.1_vimshottari_ad',
      name: 'Vimshottari',
      years: 120,
      category: 'Nakshatra-Based',
      description: 'Most popular system. 9-planet cycle based on Moon nakshatra.',
      icon: '🌙',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      id: 'yogini',
      key: '11.2_yogini_md',
      adKey: '11.2_yogini_ad',
      name: 'Yogini',
      years: 36,
      category: 'Nakshatra-Based',
      description: '8 Yogini goddesses in sequential order.',
      icon: '🧘',
      color: 'from-pink-500 to-rose-600'
    },
    {
      id: 'ashtottari',
      key: '11.3_ashtottari_md',
      adKey: '11.3_ashtottari_ad',
      name: 'Ashtottari',
      years: 108,
      category: 'Nakshatra-Based',
      description: 'Alternative 8-planet system for special nakshatras.',
      icon: '⭐',
      color: 'from-orange-500 to-amber-600'
    },
    {
      id: 'shodasottari',
      key: '11.4_shodasottari_md',
      adKey: '11.4_shodasottari_ad',
      name: 'Shodasottari',
      years: 116,
      category: 'Nakshatra-Based',
      description: '8-planet system with 11-18 year periods.',
      icon: '✨',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      id: 'chara',
      key: '11.5_chara_jaimini',
      name: 'Chara (Jaimini)',
      years: 'Variable',
      category: 'Rashi-Based',
      description: 'Sign-based movable dasha. Direction varies by odd/even ascendant.',
      icon: '♈',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      id: 'sthira',
      key: '11.6_sthira_jaimini',
      name: 'Sthira (Jaimini)',
      years: 96,
      category: 'Rashi-Based',
      description: 'Fixed sign-based system. 8 years per sign.',
      icon: '♉',
      color: 'from-green-500 to-emerald-600'
    },
    {
      id: 'narayana',
      key: '11.7_narayana',
      name: 'Narayana',
      years: 'Variable',
      category: 'Rashi-Based',
      description: 'Trikona (trine) progression system.',
      icon: '🔺',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      id: 'kalachakra',
      key: '11.8_kalachakra',
      name: 'Kalachakra',
      years: 100,
      category: 'Special',
      description: 'Time-wheel system with variable sign periods (4-21 years).',
      icon: '⏰',
      color: 'from-red-500 to-pink-600'
    },
    {
      id: 'sudarshana',
      key: '11.9_sudarshana_chakra',
      name: 'Sudarshana Chakra',
      years: '12×3',
      category: 'Special',
      description: 'Triple rotation from Lagna, Sun, and Moon simultaneously.',
      icon: '☸️',
      color: 'from-indigo-500 to-purple-600'
    }
  ];

  const selectedSystemInfo = dashaSystemsInfo.find(s => s.id === selectedSystem);
  const selectedData = data[selectedSystemInfo?.key];
  const antardashaData = data[selectedSystemInfo?.adKey];

  // Debug logging for Kalachakra
  if (selectedSystem === 'kalachakra') {
    console.log('🔍 Full data object keys:', Object.keys(data));
    console.log('🔍 selectedSystemInfo:', selectedSystemInfo);
    console.log('🔍 selectedSystemInfo.key:', selectedSystemInfo?.key);
    console.log('🔍 Kalachakra raw data:', data['11.8_kalachakra']);
    console.log('🔍 Kalachakra selectedData:', selectedData);
    console.log('🔍 All keys in selectedData:', Object.keys(selectedData || {}));
    console.log('🔍 Kalachakra mahadasas:', selectedData?.mahadasas);
    console.log('🔍 Kalachakra mahadashas (alternate spelling):', selectedData?.mahadashas);
  }

  const renderMahadashas = (systemData, systemInfo) => {
    if (!systemData) return null;

    // Handle list format (Vimshottari, Yogini)
    if (Array.isArray(systemData)) {
      if (systemData.length === 0) return <p className="text-gray-500">No periods available</p>;
      
      return (
        <div className="space-y-3">
          {systemData.map((period, idx) => {
            // Extract name (Planet, Yogini, etc.)
            const name = period.Planet || period.Yogini || period.planet || period.period_lord || 'Unknown';
            
            // Extract dates (handle both formats: "Start Date" and "start_date")
            const startDate = period['Start Date'] || period.start_date || period.start || 'N/A';
            const endDate = period['End Date'] || period.end_date || period.end || 'N/A';
            
            // Calculate duration in years
            let durationYears = period.years || period.duration_years || 0;
            if (!durationYears && startDate !== 'N/A' && endDate !== 'N/A') {
              try {
                const start = new Date(startDate);
                const end = new Date(endDate);
                const diffMs = end - start;
                durationYears = (diffMs / (1000 * 60 * 60 * 24 * 365.25)).toFixed(2);
              } catch (e) {
                durationYears = 0;
              }
            }
            
            // Get Antardashas for this Mahadasha
            const relatedADs = antardashaData ? antardashaData.filter(ad => {
              const mdName = ad.MD || ad['MD Planet'] || ad.md_planet;
              return mdName === name;
            }) : [];
            
            const isExpanded = expandedMD === idx;
            
            return (
              <div key={idx} className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedMD(isExpanded ? null : idx)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-lg text-gray-800">
                          {name}
                        </h4>
                        {relatedADs.length > 0 && (
                          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                            {relatedADs.length} ADs
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {startDate} → {endDate}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-semibold">
                        {durationYears} years
                      </span>
                      {relatedADs.length > 0 && (
                        <span className="text-gray-400">{isExpanded ? '▼' : '▶'}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Antardashas */}
                {isExpanded && relatedADs.length > 0 && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <h5 className="text-sm font-semibold text-gray-700 my-3">Antardashas ({name}):</h5>
                    <div className="space-y-2 pl-4">
                      {relatedADs.map((ad, adIdx) => {
                        const adName = ad.AD || ad['AD Planet'] || ad.ad_planet || 'Unknown';
                        const adStart = ad.Start || ad.start || ad.start_date || 'N/A';
                        const adEnd = ad.End || ad.end || ad.end_date || 'N/A';
                        
                        return (
                          <div key={adIdx} className="bg-blue-50 p-3 rounded border border-blue-100">
                            <div className="flex justify-between items-center">
                              <div className="flex-1">
                                <span className="font-semibold text-blue-700">{adName}</span>
                                <p className="text-xs text-gray-600 mt-1">
                                  {adStart.split('T')[0]} → {adEnd.split('T')[0]}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Handle dict format (new systems)
    // Note: Backend uses 'mahadasas' (no 'h') for Kalachakra, 'mahadashas' for others
    const mahadashas = systemData.mahadashas || systemData.mahadasas || [];
    
    if (mahadashas.length === 0) return <p className="text-gray-500">No periods available</p>;

    return (
      <div className="space-y-3">
        {mahadashas.map((period, idx) => {
          // Extract the sign/rasi name - Kalachakra uses "rasi" field
          const displayName = period.rasi || period.planet || period.sign || 'Unknown';
          
          // For Kalachakra, use "duration" field directly, otherwise use "duration_years"
          const durationYears = period.duration !== undefined 
            ? (typeof period.duration === 'number' ? period.duration.toFixed(2) : period.duration)
            : period.duration_years || 0;
          
          // Check if this period has antardasas
          const hasAntardasas = period.antardasas && period.antardasas.length > 0;
          const isExpanded = expandedMD === idx;
          
          return (
            <div key={idx} className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
              <div 
                className={`p-4 ${hasAntardasas ? 'cursor-pointer' : ''}`}
                onClick={() => hasAntardasas && setExpandedMD(isExpanded ? null : idx)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-lg text-gray-800">
                        {displayName}
                      </h4>
                      {period.is_balance && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                          Balance at Birth
                        </span>
                      )}
                      {hasAntardasas && (
                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                          {period.antardasas.length} ADs
                        </span>
                      )}
                    </div>
                    {period.sign_lord && (
                      <p className="text-sm text-gray-600">Lord: {period.sign_lord}</p>
                    )}
                    <p className="text-sm text-gray-600 mt-1">
                      {period.start_date} → {period.end_date}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-semibold">
                      {durationYears} years
                    </span>
                    {hasAntardasas && (
                      <span className="text-gray-400">{isExpanded ? '▼' : '▶'}</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Antardashas (for Kalachakra) */}
              {isExpanded && hasAntardasas && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <h5 className="text-sm font-semibold text-gray-700 my-3">Antardashas ({displayName}):</h5>
                  <div className="space-y-2 pl-4">
                    {period.antardasas.map((ad, adIdx) => {
                      const adName = ad.rasi || ad.sign || ad.planet || 'Unknown';
                      const adDuration = ad.duration !== undefined 
                        ? (typeof ad.duration === 'number' ? ad.duration.toFixed(2) : ad.duration)
                        : '0';
                      
                      return (
                        <div key={adIdx} className="bg-blue-50 p-3 rounded border border-blue-100">
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <span className="font-semibold text-blue-700">{adName}</span>
                              <p className="text-xs text-gray-600 mt-1">
                                {ad.start_date} → {ad.end_date}
                              </p>
                            </div>
                            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
                              {adDuration} yrs
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSudarshana = (systemData) => {
    if (!systemData) return null;

    const cycles = ['lagna_cycle', 'sun_cycle', 'moon_cycle'];
    const cycleNames = ['Lagna Cycle', 'Sun Cycle', 'Moon Cycle'];
    const cycleColors = ['border-purple-300 bg-purple-50', 'border-orange-300 bg-orange-50', 'border-blue-300 bg-blue-50'];

    return (
      <div className="space-y-6">
        <div className="bg-amber-50 border-2 border-amber-200 p-4 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> Sudarshana Chakra runs three parallel cycles simultaneously. 
            Events manifest when themes align across multiple cycles.
          </p>
        </div>

        {cycles.map((cycleKey, idx) => {
          const cycleData = systemData[cycleKey] || [];
          if (cycleData.length === 0) return null;

          return (
            <div key={cycleKey} className={`border-2 ${cycleColors[idx]} p-4 rounded-lg`}>
              <h4 className="font-bold text-lg mb-3">{cycleNames[idx]}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {cycleData.slice(0, 12).map((period, pidx) => (
                  <div key={pidx} className="bg-white p-2 rounded border border-gray-200 text-center">
                    <p className="font-semibold text-sm">{period.sign}</p>
                    <p className="text-xs text-gray-600">{period.start_date?.substring(0, 4)}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-lg border-2 border-purple-200">
        <h2 className="text-2xl font-bold text-purple-900 mb-2">
          ⏳ Complete Dasha Catalogue (9 Systems)
        </h2>
        <p className="text-sm text-gray-700">
          Comprehensive timing systems for precise event prediction. Select a system below to view details.
        </p>
      </div>

      {/* System Selector Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {dashaSystemsInfo.map((system) => {
          const isSelected = selectedSystem === system.id;
          const hasData = data[system.key] && 
            (Array.isArray(data[system.key]) ? data[system.key].length > 0 : Object.keys(data[system.key]).length > 0);

          return (
            <button
              key={system.id}
              onClick={() => setSelectedSystem(system.id)}
              className={`p-4 rounded-lg border-2 transition-all ${
                isSelected
                  ? `bg-gradient-to-r ${system.color} text-white border-transparent shadow-lg scale-105`
                  : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}
            >
              <div className="text-center">
                <div className="text-3xl mb-2">{system.icon}</div>
                <h3 className={`font-bold text-sm mb-1 ${isSelected ? 'text-white' : 'text-gray-800'}`}>
                  {system.name}
                </h3>
                <p className={`text-xs ${isSelected ? 'text-white text-opacity-90' : 'text-gray-600'}`}>
                  {system.years} {typeof system.years === 'number' ? 'years' : ''}
                </p>
                {!hasData && (
                  <p className="text-xs text-red-500 mt-1">⚠️ No data</p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected System Display */}
      {selectedSystemInfo && (
        <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden shadow-lg">
          {/* System Header */}
          <div className={`bg-gradient-to-r ${selectedSystemInfo.color} p-6`}>
            <div className="flex items-center gap-4">
              <div className="text-6xl">{selectedSystemInfo.icon}</div>
              <div className="flex-1">
                <h3 className="text-3xl font-bold text-white mb-2">
                  {selectedSystemInfo.name}
                </h3>
                <p className="text-white text-opacity-90 mb-1">
                  {selectedSystemInfo.description}
                </p>
                <div className="flex gap-4 text-sm text-white text-opacity-80">
                  <span>📊 Category: {selectedSystemInfo.category}</span>
                  <span>⏱️ Cycle: {selectedSystemInfo.years} {typeof selectedSystemInfo.years === 'number' ? 'years' : ''}</span>
                </div>
              </div>
            </div>
          </div>

          {/* System Details */}
          <div className="p-6">
            {/* Special handling for Vimshottari - use hierarchical component */}
            {selectedSystem === 'vimshottari' ? (
              <VimshottariDashaComplete dashaData={data} />
            ) : selectedData ? (
              <>
                {/* Metadata */}
                {!Array.isArray(selectedData) && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {selectedData.starting_planet && (
                        <div>
                          <p className="text-gray-600">Starting Planet</p>
                          <p className="font-bold text-gray-800">{selectedData.starting_planet}</p>
                        </div>
                      )}
                      {selectedData.starting_sign && (
                        <div>
                          <p className="text-gray-600">Starting Sign</p>
                          <p className="font-bold text-gray-800">{selectedData.starting_sign}</p>
                        </div>
                      )}
                      {selectedData.balance_at_birth !== undefined && (
                        <div>
                          <p className="text-gray-600">Balance at Birth</p>
                          <p className="font-bold text-gray-800">{selectedData.balance_at_birth} years</p>
                        </div>
                      )}
                      {selectedData.direction && (
                        <div>
                          <p className="text-gray-600">Direction</p>
                          <p className="font-bold text-gray-800">{selectedData.direction}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Mahadasha Periods */}
                <div>
                  <h4 className="text-xl font-bold text-gray-800 mb-4">
                    📅 Mahadasha Periods
                  </h4>
                  {selectedSystem === 'sudarshana' ? 
                    renderSudarshana(selectedData) : 
                    renderMahadashas(selectedData, selectedSystemInfo)
                  }
                </div>

                {/* Note */}
                {selectedData.note && (
                  <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> {selectedData.note}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">No data available for this system</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashaSystemsComplete;
